import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { ArrowLeft, Edit, Plus, Unlink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { APIClient, ProgramApi } from "@/api";
import { getFormattedDateTime } from "@/utils";
import { EditOfferProgramSheet } from "@/components/programs/EditOfferProgramSheet";
import { MerchantApi } from "@/api/merchant.api";

type OfferProgramDetailsType = {
  id?: number;
  programCode?: string;
  programName?: string;
  createdDateInMillis?: number;
  programLogoUri?: string;
  address?: string;
  country?: string;
  city?: string;
  websiteUrl?: string;
  programWebsiteUrl?: string;
};

type ProgramContactType = {
  id?: number;
  contactName?: string;
  contactEmail?: string;
  contactRole?: string;
  programContactPhoneVOList?: Array<{
    phoneCountryCode?: string;
    phoneNumber?: string;
  }>;
};

type MerchantType = {
  id: number;
  merchantName?: string;
  merchantCode?: string;
  merchantImageUri?: string;
  /** Junction / assignment id for unlink when API provides it */
  merchantProgramIdForSelectedProgram?: number;
  [key: string]: unknown;
};

function firstArrayFromRecord(obj: Record<string, unknown>, keys: string[]): unknown[] {
  for (const k of keys) {
    const v = obj[k];
    if (Array.isArray(v)) return v;
  }
  return [];
}

/**
 * Normalize offer assignment API until response shape is finalized.
 * Tries the same keys as Access Point, then common alternates.
 */
function parseOfferAssignmentResponse(data: unknown): {
  assigned: MerchantType[];
  unassigned: MerchantType[];
} {
  if (!data || typeof data !== "object") {
    return { assigned: [], unassigned: [] };
  }
  const d = data as Record<string, unknown>;
  const assignedRaw = firstArrayFromRecord(d, [
    "assignedMerchants",
    "assignedMerchantOfOfferVOList",
    "assignedMerchantVOList",
    "merchantOfOfferAssignedVOList",
  ]);
  const unassignedRaw = firstArrayFromRecord(d, [
    "unassignedMerchants",
    "unassignedMerchantOfOfferVOList",
    "unassignedMerchantVOList",
    "merchantOfOfferUnassignedVOList",
  ]);

  const normalizeRow = (r: unknown): MerchantType | null => {
    if (!r || typeof r !== "object") return null;
    const m = r as Record<string, unknown>;
    const merchantId = Number(m.id ?? m.merchantId ?? 0);
    const assignmentRaw =
      m.merchantProgramIdForSelectedProgram ??
      m.merchantProgramOfferId ??
      m.merchantProgramOfferAssignmentId ??
      m.assignmentId;
    const assignmentId =
      assignmentRaw != null && assignmentRaw !== ""
        ? Number(assignmentRaw)
        : undefined;
    return {
      ...m,
      id: Number.isFinite(merchantId) && merchantId > 0 ? merchantId : 0,
      merchantName: (m.merchantName as string) || "",
      merchantCode: (m.merchantCode as string) || "",
      merchantImageUri: (m.merchantImageUri as string) || "",
      merchantProgramIdForSelectedProgram:
        assignmentId != null && Number.isFinite(assignmentId) && assignmentId > 0
          ? assignmentId
          : undefined,
    };
  };

  const assigned = assignedRaw
    .map(normalizeRow)
    .filter((x): x is MerchantType => x != null && x.id > 0);
  const unassigned = unassignedRaw
    .map(normalizeRow)
    .filter((x): x is MerchantType => x != null && x.id > 0);

  return { assigned, unassigned };
}

const OfferProgramDetails: React.FC = () => {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState("details");
  const [isLoading, setIsLoading] = useState(false);
  const [programDetails, setProgramDetails] = useState<OfferProgramDetailsType>(
    {}
  );
  const [programContacts, setProgramContacts] = useState<ProgramContactType[]>(
    []
  );
  const [assignedMerchants, setAssignedMerchants] = useState<MerchantType[]>([]);
  const [unassignedMerchants, setUnassignedMerchants] = useState<MerchantType[]>([]);
  const [selectedMerchants, setSelectedMerchants] = useState<number[]>([]);
  const [merchantsLoading, setMerchantsLoading] = useState(false);
  const [isAddMerchantOpen, setIsAddMerchantOpen] = useState(false);
  const [isLinkConfirmationOpen, setIsLinkConfirmationOpen] = useState(false);
  const [isDelinkConfirmationOpen, setIsDelinkConfirmationOpen] = useState(false);
  /** Id sent to unAssignMerchantsOfOfferToProgramOffer (assignment id when available) */
  const [merchantToDelink, setMerchantToDelink] = useState<number | null>(null);

  const [editSheetOpen, setEditSheetOpen] = useState(false);
  const [isAddContactOpen, setIsAddContactOpen] = useState(false);
  const [isSavingContact, setIsSavingContact] = useState(false);
  const [newContact, setNewContact] = useState({
    contactName: "",
    contactEmail: "",
    contactRole: "",
    countryCode: "+1",
    phoneNumber: "",
  });

  const apiBaseUrl = (
    import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL || ""
  ).trim();

  const logoUrl = useMemo(() => {
    if (!programDetails?.programLogoUri) return "/placeholder.svg";
    if (programDetails.programLogoUri.startsWith("http")) {
      return programDetails.programLogoUri;
    }
    return `${apiBaseUrl}${programDetails.programLogoUri}`;
  }, [apiBaseUrl, programDetails?.programLogoUri]);

  const fetchProgramDetails = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const { data } = await ProgramApi.fetchOfferProgram({
        programId: Number(id),
        pageNumber: 0,
        pageSize: 5,
        sortOrder: "desc",
        sortBy: "createdDate",
      });
      const raw =
        (Array.isArray(data?.programVOs) && data.programVOs[0]) ||
        (Array.isArray(data?.programOfferVOs) && data.programOfferVOs[0]) ||
        {};
      const p = raw as Record<string, unknown>;
      setProgramDetails({
        ...(raw as OfferProgramDetailsType),
        websiteUrl:
          (p.websiteUrl as string) ||
          (p.programWebsiteUrl as string) ||
          "",
      });
    } catch (error) {
      toast.error("Failed to fetch program details");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProgramContacts = async () => {
    if (!id) return;
    try {
      const res = await APIClient(`/program/getProgramContacts?programId=${id}`);
      setProgramContacts(Array.isArray(res?.data) ? res.data : []);
    } catch (error) {
      setProgramContacts([]);
    }
  };

  const fetchMerchants = async () => {
    if (!id) return;
    setMerchantsLoading(true);
    try {
      const { data } = await MerchantApi.getMerchantOfOfferAssignmentDataFromDB({
        programId: Number(id),
        pageNumber: 0,
        pageSize: 100,
        sortOrder: "asc",
        sortBy: "createdDate",
      });
      const { assigned, unassigned } = parseOfferAssignmentResponse(data);
      setAssignedMerchants(assigned);
      setUnassignedMerchants(unassigned);
    } catch (error) {
      toast.error("Failed to fetch merchants");
      setAssignedMerchants([]);
      setUnassignedMerchants([]);
    } finally {
      setMerchantsLoading(false);
    }
  };

  useEffect(() => {
    fetchProgramDetails();
    fetchProgramContacts();
    fetchMerchants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (isAddMerchantOpen) setSelectedMerchants([]);
  }, [isAddMerchantOpen]);

  const toggleMerchantSelection = (merchantId: number) => {
    setSelectedMerchants((prev) =>
      prev.includes(merchantId)
        ? prev.filter((id) => id !== merchantId)
        : [...prev, merchantId]
    );
  };

  const handleAddMerchant = () => {
    if (selectedMerchants.length === 0) {
      toast.error("Please select at least one merchant");
      return;
    }
    setIsLinkConfirmationOpen(true);
  };

  const confirmLinkMerchants = async () => {
    if (!id) return;
    try {
      const payload = selectedMerchants.map((merchantId) => ({
        merchantId,
        programId: Number(id),
        isActive: 1,
      }));
      await MerchantApi.assignMerchantsOfOfferToProgramOffer(payload);
      await fetchMerchants();
      setSelectedMerchants([]);
      setIsLinkConfirmationOpen(false);
      setIsAddMerchantOpen(false);
      toast.success(`${payload.length} merchant(s) linked successfully`);
    } catch (error) {
      toast.error("Failed to link merchants");
    }
  };

  const handleDelinkMerchant = (merchant: MerchantType) => {
    const assignmentId = merchant.merchantProgramIdForSelectedProgram;
    const fallbackMerchantId = Number(merchant.id);
    const idForApi =
      assignmentId != null && assignmentId > 0 ? assignmentId : fallbackMerchantId;
    setMerchantToDelink(idForApi > 0 ? idForApi : null);
    setIsDelinkConfirmationOpen(true);
  };

  const confirmDelinkMerchant = async () => {
    if (merchantToDelink == null || merchantToDelink <= 0) return;
    try {
      await MerchantApi.unAssignMerchantsOfOfferToProgramOffer([merchantToDelink]);
      await fetchMerchants();
      setIsDelinkConfirmationOpen(false);
      setMerchantToDelink(null);
      toast.success("Merchant delinked successfully");
    } catch (error) {
      toast.error("Failed to delink merchant");
    }
  };

  const resetNewContact = () => {
    setNewContact({
      contactName: "",
      contactEmail: "",
      contactRole: "",
      countryCode: "+1",
      phoneNumber: "",
    });
  };

  const handleAddContact = async () => {
    if (!id) return;
    if (
      !newContact.contactName.trim() ||
      !newContact.contactEmail.trim() ||
      !newContact.contactRole.trim() ||
      !newContact.phoneNumber.trim()
    ) {
      toast.error("Please fill all contact fields");
      return;
    }

    setIsSavingContact(true);
    try {
      await APIClient(
        "/program/createOrUpdateProgramOfferContact",
        {
          programId: Number(id),
          contactName: newContact.contactName.trim(),
          contactEmail: newContact.contactEmail.trim(),
          contactRole: newContact.contactRole.trim(),
          programContactPhoneVOList: [
            {
              phoneCountryCode: newContact.countryCode.trim(),
              phoneNumber: newContact.phoneNumber.trim(),
            },
          ],
        },
        { requestType: "post" }
      );

      toast.success("Contact added successfully");
      setIsAddContactOpen(false);
      resetNewContact();
      fetchProgramContacts();
    } catch (error) {
      toast.error("Failed to add contact");
    } finally {
      setIsSavingContact(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" asChild className="bg-white">
            <RouterLink to="/offers/programs">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </RouterLink>
          </Button>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-rose-600 mb-1">
              Offer Point
            </p>
            <h1 className="text-2xl font-bold text-gray-800">Program Details</h1>
            <p className="text-gray-500">View Offer Point program information</p>
          </div>
        </div>
        <Button
          type="button"
          onClick={() => setEditSheetOpen(true)}
          disabled={!programDetails?.programCode}
          className="flex items-center gap-2 bg-rose-600 hover:bg-rose-700 shrink-0"
        >
          <Edit className="h-4 w-4" /> Edit program
        </Button>
      </div>

      <Card className="border-0 shadow-sm overflow-hidden bg-white">
        <CardContent className="p-6">
          <div className="flex items-center gap-5">
            <div className="h-20 w-20 bg-gray-100 rounded-lg flex items-center justify-center">
              <img
                src={logoUrl}
                alt={programDetails?.programName || "Program"}
                className="max-h-16 max-w-16"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = "/placeholder.svg";
                }}
              />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                {programDetails?.programName || "-"}
              </h2>
              <div className="flex items-center gap-3 mt-1 text-gray-500">
                <span className="text-sm">
                  Code: {programDetails?.programCode || "-"}
                </span>
                <span className="text-sm">
                  Created:{" "}
                  {programDetails?.createdDateInMillis
                    ? getFormattedDateTime(programDetails.createdDateInMillis)
                    : "-"}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm overflow-hidden bg-white">
        <CardContent className="p-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3 mb-6">
              <TabsTrigger value="details">Program details</TabsTrigger>
              <TabsTrigger value="contacts">Contacts</TabsTrigger>
              <TabsTrigger value="merchants">Merchants</TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="space-y-4">
              {isLoading ? (
                <p className="text-sm text-gray-500">Loading program details...</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Program Code</p>
                    <p className="text-sm font-medium">
                      {programDetails?.programCode || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Program Name</p>
                    <p className="text-sm font-medium">
                      {programDetails?.programName || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Address</p>
                    <p className="text-sm font-medium">
                      {programDetails?.address || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">City</p>
                    <p className="text-sm font-medium">
                      {programDetails?.city || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Country</p>
                    <p className="text-sm font-medium">
                      {programDetails?.country || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Website</p>
                    <p className="text-sm font-medium break-all">
                      {programDetails?.websiteUrl ||
                        programDetails?.programWebsiteUrl ||
                        "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Created Date</p>
                    <p className="text-sm font-medium">
                      {programDetails?.createdDateInMillis
                        ? getFormattedDateTime(programDetails.createdDateInMillis)
                        : "-"}
                    </p>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="contacts" className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Program Contacts</h3>
                <Button size="sm" onClick={() => setIsAddContactOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" /> Add Contact
                </Button>
              </div>

              {programContacts.length === 0 ? (
                <p className="text-sm text-gray-500">No contacts found.</p>
              ) : (
                <div className="space-y-3">
                  {programContacts.map((contact, idx) => (
                    <div
                      key={contact?.id || idx}
                      className="p-4 border rounded-md bg-gray-50"
                    >
                      <p className="font-medium text-sm">
                        {contact?.contactName || "-"}
                      </p>
                      <p className="text-sm text-gray-600">
                        {contact?.contactEmail || "-"}
                      </p>
                      <p className="text-sm text-gray-600">
                        Role: {contact?.contactRole || "-"}
                      </p>
                      <p className="text-sm text-gray-600">
                        Phone:{" "}
                        {contact?.programContactPhoneVOList?.[0]
                          ? `${contact.programContactPhoneVOList[0].phoneCountryCode || ""} ${
                              contact.programContactPhoneVOList[0].phoneNumber || ""
                            }`.trim()
                          : "-"}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="merchants" className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Linked Merchants</h3>
                <Button
                  size="sm"
                  onClick={() => setIsAddMerchantOpen(true)}
                  className="bg-rose-600 hover:bg-rose-700"
                >
                  <Plus className="h-4 w-4 mr-2" /> Link Merchant
                </Button>
              </div>

              {merchantsLoading ? (
                <p className="text-sm text-gray-500">Loading merchants...</p>
              ) : assignedMerchants.length === 0 ? (
                <p className="text-sm text-gray-500">No merchants linked yet.</p>
              ) : (
                <div className="space-y-3">
                  {assignedMerchants.map((merchant) => (
                    <div
                      key={merchant.id}
                      className="p-4 border rounded-md bg-gray-50 flex items-center justify-between gap-3"
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">
                          {merchant.merchantName || "-"}
                        </p>
                        <p className="text-xs text-gray-500">
                          {merchant.merchantCode || "No code"}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-gray-500 hover:text-red-600"
                        onClick={() => handleDelinkMerchant(merchant)}
                      >
                        <Unlink className="h-4 w-4 mr-1" /> Delink
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {id && (
        <EditOfferProgramSheet
          program={programDetails}
          programId={id}
          open={editSheetOpen}
          onOpenChange={setEditSheetOpen}
          onUpdated={(patch) => {
            setProgramDetails((prev) => ({ ...prev, ...patch }));
          }}
        />
      )}

      <Dialog open={isAddContactOpen} onOpenChange={setIsAddContactOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Contact</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Contact Name</Label>
              <Input
                value={newContact.contactName}
                onChange={(e) =>
                  setNewContact((prev) => ({
                    ...prev,
                    contactName: e.target.value,
                  }))
                }
              />
            </div>
            <div>
              <Label>Contact Email</Label>
              <Input
                value={newContact.contactEmail}
                onChange={(e) =>
                  setNewContact((prev) => ({
                    ...prev,
                    contactEmail: e.target.value,
                  }))
                }
              />
            </div>
            <div>
              <Label>Contact Role</Label>
              <Input
                value={newContact.contactRole}
                onChange={(e) =>
                  setNewContact((prev) => ({
                    ...prev,
                    contactRole: e.target.value,
                  }))
                }
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>Country Code</Label>
                <Input
                  value={newContact.countryCode}
                  onChange={(e) =>
                    setNewContact((prev) => ({
                      ...prev,
                      countryCode: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="col-span-2">
                <Label>Phone Number</Label>
                <Input
                  value={newContact.phoneNumber}
                  onChange={(e) =>
                    setNewContact((prev) => ({
                      ...prev,
                      phoneNumber: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsAddContactOpen(false);
                resetNewContact();
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleAddContact} disabled={isSavingContact}>
              {isSavingContact ? "Saving..." : "Save Contact"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isAddMerchantOpen} onOpenChange={setIsAddMerchantOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Link Merchants</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[360px] overflow-y-auto">
            {unassignedMerchants.length === 0 ? (
              <p className="text-sm text-gray-500">No merchants available to link.</p>
            ) : (
              unassignedMerchants.map((merchant) => (
                <label
                  key={merchant.id}
                  className="flex items-center justify-between p-3 border rounded-md cursor-pointer"
                >
                  <div>
                    <p className="text-sm font-medium">{merchant.merchantName || "-"}</p>
                    <p className="text-xs text-gray-500">{merchant.merchantCode || "No code"}</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={selectedMerchants.includes(merchant.id)}
                    onChange={() => toggleMerchantSelection(merchant.id)}
                  />
                </label>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddMerchantOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddMerchant} className="bg-rose-600 hover:bg-rose-700">
              Link Selected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isLinkConfirmationOpen}
        onOpenChange={setIsLinkConfirmationOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Link</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Link {selectedMerchants.length} selected merchant(s) to this program?
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsLinkConfirmationOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={confirmLinkMerchants} className="bg-rose-600 hover:bg-rose-700">
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isDelinkConfirmationOpen}
        onOpenChange={setIsDelinkConfirmationOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delink</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Are you sure you want to delink this merchant?
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDelinkConfirmationOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelinkMerchant}>
              Delink
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OfferProgramDetails;
